// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import { Form, Radio, Select, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

// Style Imports
import "./add-notification.scss";

// Local Imports
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import Label from "../../components/label/label.component";
import CustomerService from "../customers-list/customer.service";
import User from "src/models/user";
import NotificationService from "./notification.service";
import debounce from "lodash/debounce";
import Dragger from "antd/es/upload/Dragger";
import { useSelector } from "react-redux";
import PreviewNotification from "./PreviewNotification";
import EnglishForm from "./EnglishForm";
import ArabicForm from "./ArabicForm";

const { Option } = Select;

const AddNotification: React.FC = (): ReactElement => {
  const navigate = useNavigate();
  const user = useSelector((state: any) => state?.user);
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const customerServiceInstance = new CustomerService();
  const notificationServiceInstance = new NotificationService();
  const [users, setUsers] = useState<User[]>([]);
  const [submitLoginForm, setSubmitLoginForm] = useState(false);
  const [enablePreviewButton, setEnablePreviewButton] = useState(false);
  const [value, setValue] = useState(undefined);
  const [fetching, setFetching] = useState(false);
  const [uploadedAccounts, setUploadedAccounts] = useState([]);
  const [activeLanguage, setActiveLanguage] = useState("en");
  const [englishValues, setEnglishValues] = useState<{
    titleEn?: string;
    bodyEn?: string;
    imageUrl?: string;
    link?: string;
  }>({});
  const [arabicValues, setArabicValues] = useState<{
    titleAr?: string;
    bodyAr?: string;
    imageUrl?: string;
    link?: string;
  }>({});
  const [previewNotification, setPreviewNotification] = useState(false);
  const [activeEnglishPreview, setActiveEnglishPreview] = useState(
    "notification-center"
  );
  const [activeArabicPreview, setActiveArabicPreview] = useState(
    "notification-center"
  );
  const [selectedCategories, setSelectedCategories] = useState("all");

  const fetchOptions = debounce(async (searchValue) => {
    setFetching(true);
    try {
      const customerResponse =
        await customerServiceInstance.getCustomersListData(
          1,
          searchValue,
          "",
          "",
          []
        );
      setUsers(customerResponse?.users || []);
    } catch (error) {
      console.error("Error fetching options:", error);
    } finally {
      setFetching(false);
    }
  }, 300);

  useEffect(() => {
    // Check if both English and Arabic forms have required values
    const currentValues = form.getFieldsValue();
    const englishValid =
      (currentValues.titleEn && currentValues.bodyEn) ||
      (englishValues?.titleEn && englishValues?.bodyEn);
    const arabicValid = currentValues.titleAr && currentValues.bodyAr;

    // Preview button requires both English and Arabic to be complete
    setEnablePreviewButton(englishValid && arabicValid);

    // Submit form validation (for the send button) - keep original logic
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmitLoginForm(true);
      },
      () => {
        setSubmitLoginForm(false);
      }
    );
  }, [values, form, englishValues]);

  const handleSearch = (searchValue) => {
    if (searchValue) {
      fetchOptions(searchValue);
    } else {
      setUsers([]);
    }
  };

  const handleChange = (newValue) => {
    setValue(newValue);
  };

  const handleFileUpload = (file) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const workbook = XLSX.read(event.target.result, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const sheetData = XLSX.utils.sheet_to_json(sheet);

      setUploadedAccounts(
        sheetData.map((item) => Object.values(item)[0]?.toString())
      );
    };

    reader.readAsBinaryString(file);
  };

  const onFinish = async (values) => {
    let body = {};
    if (values.titleAr) {
      body = { ...values };
    } else {
      body = { ...englishValues, ...arabicValues };
    }
    if (selectedCategories !== "upload") {
      await notificationServiceInstance.addNotification(body);
    } else {
      await notificationServiceInstance.addNotificationUsingAccountCodes({
        ...body,
        accountCodes: uploadedAccounts,
      });
    }
  };

  return (
    <section className="add-notification-container flex flex-col">
      <BreadcrumbComponent />
      <div className="flex flex-col bg-white p-[2%] rounded-[20px]">
        <h3 className=" text-black-500 text-base font-semibold border-b-[1px] border-gray-900 pb-3 mb-5">
          Create a new message
        </h3>
        <Form
          form={form}
          className="flex flex-col flex-wrap justify-between"
          onFinish={onFinish}
        >
          <div className="flex items-center justify-between">
            <Form.Item name="categories" className="w-fit">
              <Radio.Group
                defaultValue={"all"}
                onChange={(e) => setSelectedCategories(e.target.value)}
              >
                <Radio value={"all"}>All</Radio>
                <Radio value={"customers"}>Customers</Radio>
                <Radio value={"upload"}>Upload CSV file</Radio>
              </Radio.Group>
            </Form.Item>

            {enablePreviewButton && (
              <button
                type="button"
                onClick={() => {
                  setPreviewNotification(true);
                  setArabicValues(form.getFieldsValue());
                }}
                className="p-2.5 bg-white rounded outline outline-[0.59px] outline-offset-[-0.59px] outline-blue-500"
              >
                <div className=" text-blue-500 text-xs font-bold whitespace-nowrap">
                  Preview Notification
                </div>
              </button>
            )}
          </div>

          {form.getFieldValue("categories") === "upload" && (
            <div className="flex-col w-full">
              <Label text="Customers CSV file" />
              <Form.Item
                name="agreementFileEn"
                rules={[
                  {
                    required: true,
                    message: "Please upload the Customers CSV file",
                  },
                ]}
              >
                <Dragger
                  beforeUpload={() => false}
                  customRequest={() => false}
                  multiple={false}
                  accept=".csv,.xlsx,.xls"
                  maxCount={1}
                  onChange={(info) => handleFileUpload(info.file)}
                >
                  <p className="ant-upload-text h-20 flex items-center justify-center">
                    Click or drag file to this area to upload
                  </p>
                </Dragger>
              </Form.Item>
            </div>
          )}

          {form.getFieldValue("categories") === "customers" && (
            <div className="flex-col w-full md:w-[40%]">
              <Label text="Customers" />
              <Form.Item
                name="users"
                rules={[
                  {
                    required: true,
                    message: "Please slect Customers!",
                  },
                ]}
              >
                <Select
                  showSearch
                  value={value}
                  placeholder="Select Customers"
                  defaultActiveFirstOption={false}
                  showArrow={false}
                  filterOption={false}
                  onSearch={handleSearch}
                  onChange={handleChange}
                  notFoundContent={fetching ? <Spin size="small" /> : null}
                  style={{ width: "100%" }}
                  mode="multiple"
                  maxTagCount={2}
                >
                  {users.map((user) => (
                    <Option value={user?.id} key={user?.id}>
                      <span>
                        {user?.firstName} {user?.lastName}
                      </span>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          )}

          {!previewNotification ? (
            activeLanguage === "en" ? (
              <EnglishForm 
                form={form} 
                user={user} 
                onImageRemove={() => {
                  // Clear image from saved English values when removed
                  setEnglishValues(prev => ({
                    ...prev,
                    imageUrl: ""
                  }));
                }}
              />
            ) : (
              <ArabicForm 
                form={form} 
                user={user} 
                onImageRemove={() => {
                  // Clear image from saved Arabic values when removed
                  setArabicValues(prev => ({
                    ...prev,
                    imageUrl: ""
                  }));
                }}
              />
            )
          ) : null}

          {previewNotification && (
            <PreviewNotification
              activeEnglishPreview={activeEnglishPreview}
              activeArabicPreview={activeArabicPreview}
              setActiveEnglishPreview={setActiveEnglishPreview}
              setActiveArabicPreview={setActiveArabicPreview}
              englishValues={englishValues}
              arabicValues={arabicValues}
            />
          )}

          <div className="self-stretch flex flex-col lg:flex-row justify-between items-start lg:items-center mt-7">
            <button
              onClick={() => navigate("/cards")}
              className="w-48 self-stretch py-4 flex justify-center items-center gap-2"
            >
              <div className="text-center justify-center text-neutral-400 text-sm font-semibold font-['Montserrat'] underline leading-none">
                Cancel
              </div>
            </button>
            <div className="flex justify-end items-start gap-5">
              {(activeLanguage === "ar" || previewNotification) && (
                <button
                  onClick={() => {
                    if (previewNotification) {
                      setPreviewNotification(false);
                    } else {
                      // Restore English form values when going back from Arabic
                      form.setFieldsValue({
                        titleEn: englishValues.titleEn || "",
                        bodyEn: englishValues.bodyEn || "",
                        imageUrl: englishValues.imageUrl || "",
                        link: englishValues.link || "",
                      });
                      setActiveLanguage("en");
                    }
                  }}
                  type="button"
                  className="w-48 h-12 p-4 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-blue-500 flex justify-center items-center gap-2.5"
                >
                  <div className="text-center justify-start text-blue-500 text-sm font-bold font-['Montserrat'] leading-none">
                    Back
                  </div>
                </button>
              )}
              <button
                type="button"
                disabled={
                  activeLanguage === "en"
                    ? !form.getFieldValue("titleEn") ||
                      !form.getFieldValue("bodyEn")
                    : !submitLoginForm
                }
                onClick={() => {
                  if (activeLanguage === "en") {
                    // Save current English values before switching
                    const currentEnglishValues = {
                      titleEn: form.getFieldValue("titleEn"),
                      bodyEn: form.getFieldValue("bodyEn"),
                      imageUrl: form.getFieldValue("imageUrl"),
                      link: form.getFieldValue("link"),
                    };
                    setEnglishValues(currentEnglishValues);
                    setActiveLanguage("ar");
                  } else {
                    // Combine English and Arabic values for submission
                    const arabicValues = form.getFieldsValue();
                    const allValues = {
                      ...englishValues,
                      ...arabicValues,
                    };
                    onFinish(allValues);
                  }
                }}
                className="w-48 h-12 p-3 bg-blue-500 rounded-lg flex justify-center items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="text-center justify-start text-white text-sm font-bold font-['Montserrat'] leading-none">
                  {activeLanguage === "en" ? "Next" : "Send notification"}
                </div>
              </button>
            </div>
          </div>
        </Form>
      </div>
    </section>
  );
};

export default AddNotification;
